/**
 * LoginPage Component
 *
 * User authentication page with email/password and Google OAuth login.
 * Handles redirect after successful login using ?redirect query param.
 *
 * REDIRECT FLOW:
 * ────────────────────────────────────────────────────────────────
 * 1. User tries to access protected route (e.g., /admin/ab-tests)
 * 2. Loader detects no authentication
 * 3. Redirects to /login?redirect=/admin/ab-tests
 * 4. User logs in successfully
 * 5. Navigate to redirectTo (/admin/ab-tests)
 * 6. Loader runs again, user is authenticated
 * 7. Protected page renders
 *
 * AUTHENTICATION METHODS:
 * ────────────────────────────────────────────────────────────────
 * 1. Email/Password: Traditional form-based login
 * 2. Google OAuth: GoogleLoginButton component
 * 3. Facebook OAuth: FacebookLoginButton component (SCRUM-1827)
 *
 * TODO: Add additional OAuth providers
 * - Apple Login (iOS users)
 * - Microsoft/Azure AD (enterprise customers)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLoaderData } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import GoogleLoginButton from '../../components/auth/GoogleLoginButton';
import FacebookLoginButton from '../../components/auth/FacebookLoginButton';
import ForgotPasswordForm from '../../components/auth/ForgotPasswordForm';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: _user, isPreLaunchMode } = useLoaderData(); // From loginLoader
  const { login, loginWithGoogle, loginWithFacebook } = useAuth();

  // Form state
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Get redirect destination from query params
  const redirectTo = searchParams.get('redirect') || '/';

  // SCRUM-1722: Cancel any Google One Tap / FedCM auto-prompt on mount.
  // The GSI client loaded by GoogleOAuthProvider may trigger FedCM passively,
  // displaying a prior user's name+email without authentication (PII leak).
  useEffect(() => {
    try {
      if (window.google?.accounts?.id?.cancel) {
        window.google.accounts.id.cancel();
      }
    } catch (_e) {
      // GSI not yet loaded — no prompt to cancel
    }
  }, []);

  /**
   * Handle email/password login
   */
  const handleSubmit = async (  _e) => {
    _e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Redirect to intended destination after successful login
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  /**
   * Handle Google OAuth success
   */
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle(credentialResponse.credential);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError('Google login failed. Please try again.');
      setLoading(false);
    }
  };

  /**
   * Handle Google OAuth error
   */
  const handleGoogleError = () => {
    setError('Google login failed. Please try again or use email/password.');
  };

  /**
   * Handle Facebook OAuth success (SCRUM-1827)
   */
  const handleFacebookSuccess = async ({ accessToken }) => {
    setError('');
    setLoading(true);

    try {
      await loginWithFacebook(accessToken);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError('Facebook login failed. Please try again.');
      setLoading(false);
    }
  };

  /**
   * Handle Facebook OAuth error (SCRUM-1827)
   */
  const handleFacebookError = (err) => {
    setError(
      (err && err.message) ||
        'Facebook login failed. Please try again or use email/password.'
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md w-full space-y-8">
          {mode === 'forgot' && (
            <ForgotPasswordForm setAuthMode={(m) => setMode(m === 'signin' ? 'login' : m)} />
          )}
          {mode === 'login' && (<>
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-600">
              Sign in to your OutdoorGuides account
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Redirect notice */}
          {redirectTo !== '/' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                Please sign in to continue to your destination.
              </p>
            </div>
          )}

          {/* SCRUM-1779: Cognito Managed Login button removed — consumer auth
              is email/password + Google only. Cognito is the invisible OAuth
              layer, not a consumer-facing auth method. Admin IC login (/admin/login)
              is a separate flow and is not affected. */}
          <div className="space-y-4">
            {/* Google OAuth */}
            <GoogleLoginButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={loading}
            />

            {/* Facebook OAuth (SCRUM-1827) */}
            <FacebookLoginButton
              onSuccess={handleFacebookSuccess}
              onError={handleFacebookError}
              disabled={loading}
            />

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@example.com"
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>

              {/* Forgot password link */}
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </div>

          {/* Sign up link - Hidden during full pre-launch mode */}
          {!isPreLaunchMode && (
            <div className="text-center text-sm">
              <span className="text-gray-600">Don&apos;t have an account? </span>
              <a
                href="/guides/signup"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Sign up as a guide
              </a>
            </div>
          )}
          </>)}
        </div>
      </main>

      <Footer />
    </div>
  );
}
